import React, { useEffect, useRef, useState } from 'react'
import { CanBeAnything } from '../../../types'
import './usage-reporting.scss'
import { InfoTooltip } from '../../atoms/info-tooltip/info-tooltip'
import { Button } from '../../atoms/button/button'
import { t } from '../../../utils/i18n'
import CustomDropdown, { DropdownOption } from '../dropdown/dropdown'
import CustomTable from '../table/table'
import {
  BUSINESS_DETAILS,
  CREATIVE_REQUEST_TRENDLINE,
  CREATIVE_TABLE_DATA,
  DIAGNOSTIC_AD_ACCOUNT_LIST,
  DIAGNOSTIC_ADVERTISER_LIST,
  DIAGNOSTIC_MEDIA_DATA,
} from '../../../constants/routes'
import { useAuth } from '../../../customHooks/useAuth'
import axios from '../../../utils/axios'
import { AssetDateFilter } from '../../organisms/asset-date-filter/asset-date-filter'
import { mapAnalyzeData } from '../../../containers/utils'
import { AnalyzeData, AnalyzeTable } from '../analyze-table/AnalayzeTable'
import { ReactComponent as Download } from '../../../assets/svgs/download.svg'
import { showToast } from '../../atoms/toaster/toaster'

interface IUsageReporting {
  businessId: string
}

interface AdAccount {
  accountId: string
  accountName: string
}

export interface MediaData {
  published_batch_count: number
  total_batch_count: number
  published_campaign_count: number
  total_campaign_count: number
  published_ad_count: number
  total_ad_count: number
}

export interface CreativeData {
  totalNumberOfNonApprovedBatches: number
  totalNumberOfApprovedBatches: number
  avgNumberOfAdGroupsPerBatch: number
  totalRequestedCreativeRequests: number
  totalCompletedCreativeRequests: number
  totalApprovedAdGroups: number
  totalRejectedAdGroups: number
  totalCollabrativeUsers: number
}

export const UsageReporting = ({ businessId }: IUsageReporting) => {
  const { user } = useAuth()
  const [isLoadingMedia, setIsLoadingMedia] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [advertisersList, setAdvertisersList] = useState<DropdownOption[]>([])

  const [selectedAdvertiser, setSelectedAdvertiser] = useState<string>('')
  // eslint-disable-next-line
  const [selectedAdAccount, setSelectedAdAccount] = useState<DropdownOption | null>(null)
  const [adAccountList, setAdAccountList] = useState<AdAccount[]>([])
  const [mediaQuery, setMediaQuery] = useState<string>('getData=ALL')
  const [mediaData, setMediaData] = useState<MediaData | null>(null)
  const [creativeData, setCreativeData] = useState<CreativeData | null>(null)
  const [analyzeData, setAnalyzeData] = useState<AnalyzeData[]>()

  const startDateRef = useRef<string>('')
  const endDateRef = useRef<string>('')

  useEffect(() => {
    getAdvertiserList()
    getMediaData(mediaQuery)
    getCreativeData('parentAdvertiserName=ALL')
  }, [businessId])

  useEffect(() => {
    getAdAccountList('')
  }, [])

  const getAdvertiserList = async () => {
    const url = DIAGNOSTIC_ADVERTISER_LIST.replace(':businessAccountId', businessId)
    const config = {
      method: 'GET',
      url,
      headers: {
        'x-user': user?.email,
        'x-access-token': user?.tokens?.accessToken?.token,
        'x-source': user?.isSSO ? 'sso' : '',
      },
    }

    const res = await axios(config)
    if (res?.data?.data) {
      setAdvertisersList(
        res.data.data?.length > 1 ? [{ id: 'all', name: 'All' }, ...res.data.data] : res?.data?.data
      )
    }
  }

  const getAdAccountList = async (advertiser: string) => {
    const url =
      advertiser && advertiser.toLowerCase() !== 'all'
        ? `${DIAGNOSTIC_AD_ACCOUNT_LIST.replace(':businessAccountId', businessId)}?parentAdvertiserName=${advertiser}`
        : DIAGNOSTIC_AD_ACCOUNT_LIST.replace(':businessAccountId', businessId)

    const config = {
      method: 'GET',
      url,
      headers: {
        'x-user': user?.email,
        'x-access-token': user?.tokens?.accessToken?.token,
        'x-source': user?.isSSO ? 'sso' : '',
      },
    }

    const res = await axios(config)
    if (res?.data?.data) {
      setAdAccountList(res.data.data.onbordedAdAccounts)
    }

    // if (selectedAdAccount) {
    //   const found = res.data.data.onbordedAdAccounts.find((onBoardedAccount: CanBeAnything) => {
    //     return onBoardedAccount.accountId === selectedAdAccount.id
    //   })
    //   if (!found) {
    //     setSelectedAdAccount(null)
    //   }
    // }
  }

  const getMediaData = async (query: string) => {
    const url = `${DIAGNOSTIC_MEDIA_DATA}?${query}`.replace(':businessAccountId', businessId)

    const config = {
      method: 'GET',
      url,
      headers: {
        'x-user': user?.email,
        'x-access-token': user?.tokens?.accessToken?.token,
        'x-source': user?.isSSO ? 'sso' : '',
      },
    }

    const res = await axios(config)
    if (res?.data?.data) {
      setIsLoading(false)
      setMediaData(res.data.data.data)
    }
  }

  const getCreativeData = async (query: string) => {
    const url = `${CREATIVE_TABLE_DATA}?${query}`.replace(':businessAccountId', businessId)

    const config = {
      method: 'GET',
      url,
      headers: {
        'x-user': user?.email,
        'x-access-token': user?.tokens?.accessToken?.token,
        'x-source': user?.isSSO ? 'sso' : '',
      },
    }

    const res = await axios(config)
    console.log(res, 'getCreativeData')

    if (res?.data?.data) {
      setIsLoading(false)
      setCreativeData(res.data.data.data)
    }
  }

  const handleAdvertiserChange = (option: DropdownOption) => {
    setSelectedAdvertiser(option.name)
    getAdAccountList(option.name)
    callAnalyzeAPI(businessId, startDateRef.current, endDateRef.current, option.name)
  }

  const handleAdAccountChange = (option: DropdownOption) => {
    setSelectedAdAccount(option)
    const query = `adAccountId=${option.id}`
    setMediaQuery(query)
    getMediaData(query)
    getCreativeData(query)
    callAnalyzeAPI(
      businessId,
      startDateRef.current,
      endDateRef.current,
      selectedAdvertiser,
      option.id
    )
  }

  const transformedADAccountList: DropdownOption[] = adAccountList?.map((ad) => ({
    id: ad.accountId,
    name: ad.accountName,
  }))

  const columns = [
    { key: 'particular', label: t('particular'), flexGrow: 1 },
    { key: 'to.meta', label: t('to.meta'), flexGrow: 1 },
    { key: 'bm.status', label: t('bm.status'), flexGrow: 1 },
  ]

  const Creativecolumns = [
    { key: 'particular', label: t('particular'), flexGrow: 1 },
    { key: 'insights', label: t('Insights'), flexGrow: 1 },
  ]
  const creativeTableData = creativeData
    ? [
        {
          particular: 'No of Creative Batches',
          insights: `${creativeData?.totalNumberOfNonApprovedBatches} (Started) / ${creativeData?.totalNumberOfApprovedBatches}  (Approved)`,
        },
        {
          particular: 'Average no of AdGroups per Batch',
          insights: creativeData?.avgNumberOfAdGroupsPerBatch,
        },
        // {
        //   particular: 'Average Approval Time',
        //   insights: '5 min (dummy data)',
        // },
        {
          particular: 'Creative Analysis Requests Services',
          insights: `${creativeData?.totalRequestedCreativeRequests} (Requested) / ${creativeData?.totalCompletedCreativeRequests} (Ready)`,
        },
        {
          particular: 'Approved vs Rejected',
          insights: `${creativeData?.totalApprovedAdGroups} (Approved) / ${creativeData?.totalRejectedAdGroups} (Rejected)`,
        },
        {
          particular: 'Total Collaborative Users',
          insights: creativeData?.totalCollabrativeUsers,
        },
      ]
    : []
  const data = mediaData
    ? [
        {
          particular: t('batches'),
          'to.meta': mediaData.published_batch_count,
          'bm.status': mediaData.total_batch_count,
        },
        {
          particular: t('campaigns'),
          'to.meta': mediaData.published_campaign_count,
          'bm.status': mediaData.total_campaign_count,
        },
        {
          particular: t('ads'),
          'to.meta': mediaData.published_ad_count,
          'bm.status': mediaData.total_ad_count,
        },
      ]
    : []

  const callAnalyzeAPI = async (
    businessIdParam: string,
    startDate: string,
    endDate: string,
    parentAdvertiserName = '',
    adAccountId = ''
  ) => {
    try {
      setIsLoadingMedia(true)
      const config = {
        method: 'GET',
        url:
          parentAdvertiserName || adAccountId
            ? `${BUSINESS_DETAILS}/${businessIdParam}/analyze/data?&startDate=${startDate}&endDate=${endDate}&parentAdvertiserName=${parentAdvertiserName}&adAccountId=${adAccountId}`
            : `${BUSINESS_DETAILS}/${businessIdParam}/analyze/data?&startDate=${startDate}&endDate=${endDate}`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }
      const analyzeData = await axios(config)
      setAnalyzeData(mapAnalyzeData(analyzeData?.data?.data))
    } catch {
      setIsLoadingMedia(false)
    } finally {
      setIsLoadingMedia(false)
    }
  }

  const getDateRange = async (startDate: string, endDate: string) => {
    startDateRef.current = startDate
    endDateRef.current = endDate
    if (businessId) {
      callAnalyzeAPI(businessId, startDate, endDate)
    }
  }

  const exportClick = async () => {
    try {
      const config = {
        method: 'POST',
        url: `${CREATIVE_REQUEST_TRENDLINE}`,
        data: {
          parameters: {
            businessAccountId: businessId,
          },
        },
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const result = await axios(config)
      const reqId = result?.data?.data?.data?.requestId
      pollingForRequest(reqId)
      showToast({
        message: 'Success',
        description: t('Request Received!,Your file will auto download.'),
      })
      //  return result
    } catch {
      console.log('Unable to create export request')
      showToast({
        message: t('Request Failed!, Please try again.'),
        isError: true,
      })
    }
  }

  const pollingForRequest = async (reqId: CanBeAnything) => {
    const interval = 5000 // 5 seconds
    const maxDuration = 60000 // 1 minute
    const startTime = Date.now()

    const poll = async () => {
      try {
        const config = {
          method: 'get',
          url: `${CREATIVE_REQUEST_TRENDLINE}/${reqId}`,
          headers: user?.isSSO
            ? {
                'x-user': user?.email,
                'x-access-token': user?.tokens?.accessToken?.token,
                'x-source': 'sso',
              }
            : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
        }

        const result = await axios(config)
        const presignedURL = result?.data?.data?.data?.presignedUrl
        if (presignedURL) {
          // Stop polling and download the file
          clearInterval(pollingInterval)
          window.location.href = presignedURL // This will trigger the download
          showToast({
            message: t('File Downloaded Successfully!'),
          })
          return
        }

        // Check if the maximum duration has been reached
        if (Date.now() - startTime >= maxDuration) {
          clearInterval(pollingInterval)
          console.log('Polling stopped after 1 minute without finding presignedURL')
          showToast({
            message: t('Request Failed!, Please try again.'),
            isError: true,
          })
        }
      } catch (error) {
        console.log('Unable to create session Id', error)
        showToast({
          message: t('Request Failed!, Please try again.'),
          isError: true,
        })
      }
    }

    const pollingInterval = setInterval(poll, interval)
    poll() // Initial call to start polling immediately
  }
  return (
    <div className="usage-reporting-wrapper">
      <div className="usage-reporting">
        <AssetDateFilter getDateRange={getDateRange} />
        <div className="dropdown-wrapper">
          <div className="title-info">
            <p className="label-input">{t('advertiser.name')}</p>
            <InfoTooltip
              styleClass="input-info"
              tooltipContent={t('advertiser.dropdown.tooltip')}
              placement={'right'}
            />
          </div>
          <div className="dropdown">
            <CustomDropdown
              id="advertiser-selector"
              className="label-input"
              label=""
              placeholder={t('advertiser.dropdown.placeholder')}
              options={advertisersList}
              onSelect={handleAdvertiserChange}
            />
          </div>
        </div>
        <div className="dropdown-wrapper">
          <div className="title-info">
            <p className="label-input">{t('ad.account.name')}</p>
            <InfoTooltip
              styleClass="input-info"
              tooltipContent={t('ad.account.dropdown.tooltip')}
              placement={'left'}
            />
          </div>
          <div className="dropdown">
            <CustomDropdown
              id="ad.account-selector"
              className="label-input"
              label=""
              placeholder={t('ad.account.dropdown.placeholder')}
              options={transformedADAccountList}
              onSelect={handleAdAccountChange}
            />
          </div>
        </div>
      </div>
      <hr className="link-divider divider" />
      <div>
        <h2>{t('analyze')}</h2>
        <AnalyzeTable analyzeData={analyzeData} isLoading={isLoadingMedia} />
      </div>
      <div>
        <div className="creative-table">
          <h2>{t('Creative')}</h2>
          <Button
            ButtonIcon={<Download />}
            label="Export"
            buttonWithText={true}
            removeDefaultCss
            // disabled={true}
            // ariaLabel="go-back"
            // className="right-btn"
            handleClick={() => exportClick()}
          />
        </div>

        <CustomTable data={creativeTableData} columns={Creativecolumns} isLoading={isLoading} />
      </div>
      <h2 style={{ marginTop: '1rem' }}>{t('media')}</h2>
      <CustomTable data={data} columns={columns} isLoading={isLoading} />
    </div>
  )
}

export default UsageReporting
